import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { ApolloError, gql, useMutation } from '@apollo/client';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import { getOperationName } from 'apollo-utilities';
import {
  NewOrder_ReorderMutation,
  NewOrder_ReorderMutationVariables,
} from '@/types';
import {
  ORDER_CONTEXT_QUERY,
  ORDER_FRAGMENT,
} from '@/contexts/new-order-context-queries';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import {
  shouldShowOrder,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text, View } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMaybeOrderDrawerContext } from '@/contexts/order-drawer-context';
import { useNavigation } from '@react-navigation/native';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useToasts } from '@/contexts/toast-context';

export const REORDER_ALL_ITEMS_MUTATION = gql`
  mutation NewOrder_Reorder(
    $deliveryDate: String
    $timeslotEnd: String
    $timeslotStart: String
    $fulfillmentType: String
    $previousOrderUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    reorder(
      delivery_date: $deliveryDate
      fulfillment_timeslot_end: $timeslotEnd
      fulfillment_timeslot_start: $timeslotStart
      fulfillment_type: $fulfillmentType
      previous_order_id: $previousOrderUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      order {
        ...NewOrder_OrderFields
      }
    }
  }

  ${ORDER_FRAGMENT}
`;

type Navigation = StackNavigationProp<
  AppContainerStackParams,
  APP_CONTAINER_ROUTES.NewOrder
>;

interface Props {
  onClose: () => void;
  orderUUID: string;
}

export const OrderDetailReorder: React.FC<Props> = ({ onClose, orderUUID }) => {
  const [styles] = useStyles();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { selectedFulfillment, updateAndClearQuantity } =
    useNewOrderActionsContext();
  const { setDraftOrder } = useNewOrderContext();
  const { isSmallLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();
  const orderDrawerContext = useMaybeOrderDrawerContext();
  const navigation = useNavigation<Navigation>();

  const [reorder, { loading }] = useMutation<
    NewOrder_ReorderMutation,
    NewOrder_ReorderMutationVariables
  >(REORDER_ALL_ITEMS_MUTATION, {
    refetchQueries: [getOperationName(ORDER_CONTEXT_QUERY) as string],
    variables: {
      deliveryDate: selectedFulfillment?.date,
      fulfillmentType: selectedFulfillment?.type,
      previousOrderUUID: orderUUID,
      restaurantUUID,
      supplierUUID,
      timeslotEnd: selectedFulfillment?.timeslot?.endTime,
      timeslotStart: selectedFulfillment?.timeslot?.startTime,
    },
  });

  const submitReorder = useCallback(async () => {
    try {
      PerfTiming.start(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
        entryPoint: 'order_detail_reorder_button',
      });
      const result = await reorder();
      if (result.data?.reorder.order) {
        setDraftOrder(result.data.reorder.order);
        onClose();

        if (isSmallLayout) {
          navigation.navigate(APP_CONTAINER_ROUTES.NewOrder);
          return;
        }

        if (orderDrawerContext) {
          try {
            const result = await updateAndClearQuantity('drawer_header');
            if (shouldShowOrder(result)) {
              orderDrawerContext.openDrawer();
            }
          } catch {
            throw new Error('Unable to open drawer after reordering');
          }
        }
      }
    } catch (e) {
      if (e instanceof ApolloError && getMessageFromGQLError(e)) {
        showErrorToast(getMessageFromGQLError(e));
      } else {
        showErrorToast();
      }
    }
  }, [
    isSmallLayout,
    navigation,
    onClose,
    orderDrawerContext,
    reorder,
    setDraftOrder,
    showErrorToast,
    updateAndClearQuantity,
  ]);

  return (
    <BaseActionSheet isVisible onClose={onClose}>
      <Text style={styles.reorderTitle}>
        {i18n.t('reorder.confirmation_title')}
      </Text>
      <Text style={styles.reorderText}>
        {i18n.t('reorder.confirmation_message')}
      </Text>
      <View style={styles.reorderButtonsWrapper}>
        <Button
          loading={loading}
          onPress={submitReorder}
          trackingData={{
            name: 'reorder_add_to_cart_button',
            properties: {
              orderUUID,
            },
          }}
        >
          {i18n.t('reorder.confirmation_button')}
        </Button>
        <Button
          onPress={onClose}
          trackingData={{
            name: 'reorder_cancel_button',
            properties: {
              orderUUID,
            },
          }}
          type="secondary"
        >
          {i18n.t('cancel')}
        </Button>
      </View>
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  reorderButtonsWrapper: {
    gap: 8,
  },
  reorderText: {
    ...FONTS.primary,
    color: theme.sk.color.text.secondary,
    fontSize: theme.fontSizes.medium,
    marginBottom: 24,
  },
  reorderTitle: {
    ...FONTS.primarySemiBold,
    fontSize: 21,
    marginBottom: 8,
  },
}));
